import {useAppOverrides} from 'contexts/AppOverridesContext';
import {doc, getFirestore, setDoc} from 'firebase/firestore';
import {useAuthUid} from 'hooks/user/useUserId';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {useDocumentData} from 'react-firebase-hooks/firestore';
import {z} from 'zod';

/** The choices a user can make for the app's color scheme. */
export const COLOR_SCHEME_PREFERENCES = ['light', 'dark', 'system'] as const;

/** Zod schema for {@link ColorSchemePreference}. */
export const ColorSchemePreferenceSchema = z.enum(COLOR_SCHEME_PREFERENCES);

/** A user's stored color-scheme choice. `system` follows the OS setting. */
export type ColorSchemePreference = z.infer<typeof ColorSchemePreferenceSchema>;

/** The scheme actually in effect after resolving `system` and availability. */
export type ResolvedColorScheme = 'light' | 'dark';

/**
 * Shape of the per-user appearance settings document. Parsed leniently so a
 * document written by a newer client (with fields this schema doesn't know)
 * still loads, and an unknown `colorScheme` value falls back to the default.
 */
const AppearanceSettingsSchema = z.object({
  colorScheme: ColorSchemePreferenceSchema.optional(),
});

/**
 * Builds a reference to a user's appearance settings document. Lives beside
 * the Math IDE's settings doc under the self-only `users/{uid}/settings`
 * subcollection (see the Firestore rules for that path).
 *
 * @param userId - The signed-in user's uid. When `null`, no reference is
 *     built and `null` is returned so callers can pass it straight to
 *     `useDocumentData`.
 * @return The document reference, or `null` when there is no user.
 * @throws Never.
 */
export function getAppearanceSettingsRef(userId: string | null) {
  if (!userId) {
    return null;
  }
  return doc(getFirestore(), 'users', userId, 'settings', 'appearance');
}

/** Value exposed by {@link ColorSchemeProvider}. */
export interface ColorSchemeContextData {
  /** The user's stored choice, or the deploy default when none is stored. */
  preference: ColorSchemePreference;
  /**
   * The scheme in effect. Always `light` when the deploy supplies no dark
   * theme, whatever the preference says.
   */
  resolvedScheme: ResolvedColorScheme;
  /**
   * Whether the deploy supplies a dark theme (`AppOverrides.courseThemeDark`).
   * When false the preference control should be hidden: choosing has no effect.
   */
  isDarkAvailable: boolean;
  /**
   * Stores a new preference for the signed-in user. A no-op (logged at debug
   * level) when no user is signed in.
   */
  setPreference: (preference: ColorSchemePreference) => void;
}

const ColorSchemeContext = createContext<ColorSchemeContextData>({
  preference: 'light',
  resolvedScheme: 'light',
  isDarkAvailable: false,
  setPreference: () => {},
});

/**
 * Tracks whether the OS currently prefers a dark color scheme.
 *
 * @return `true` while `prefers-color-scheme: dark` matches; `false` otherwise,
 *     including in environments without `matchMedia`.
 * @throws Never.
 */
function useSystemPrefersDark(): boolean {
  const getMatch = () =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-color-scheme: dark)').matches
      : false;
  const [prefersDark, setPrefersDark] = useState<boolean>(getMatch);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (event: MediaQueryListEvent) => {
      setPrefersDark(event.matches);
    };
    media.addEventListener('change', onChange);
    return () => {
      media.removeEventListener('change', onChange);
    };
  }, []);
  return prefersDark;
}

/**
 * Resolves the active color scheme for the signed-in user and applies it to
 * the document.
 *
 * The preference is read live from the user's appearance settings document,
 * so it follows the user across devices. Until that document loads, the
 * deploy default (`AppOverrides.defaultColorScheme`) applies, so a user who
 * has overridden the default may see one repaint after sign-in.
 *
 * This provider only resolves state. Nothing goes dark until a surface opts
 * in: `CourseThemeProvider` overlays the dark theme when told to, and
 * {@link useBootstrapColorScheme} flips Bootstrap's components for the same
 * surface. The course shell (`CoursePage`) is the only opt-in today, so the
 * splash, login, application, and full-screen focus pages stay light.
 */
export function ColorSchemeProvider({children}: {children: ReactNode}) {
  const {courseThemeDark, defaultColorScheme} = useAppOverrides();
  const uid = useAuthUid();
  const settingsRef = useMemo(() => getAppearanceSettingsRef(uid), [uid]);
  const [settingsData] = useDocumentData(settingsRef);
  const systemPrefersDark = useSystemPrefersDark();
  // Local echo of the last write so the UI flips immediately rather than
  // waiting for the Firestore listener to deliver the stored value.
  const [pendingPreference, setPendingPreference] =
    useState<ColorSchemePreference | null>(null);

  const storedPreference = useMemo(() => {
    const parsed = AppearanceSettingsSchema.safeParse(settingsData ?? {});
    return parsed.success ? parsed.data.colorScheme : undefined;
  }, [settingsData]);

  const isDarkAvailable = courseThemeDark !== undefined;
  const preference: ColorSchemePreference =
    pendingPreference ?? storedPreference ?? defaultColorScheme ?? 'light';
  const wantsDark =
    preference === 'dark' || (preference === 'system' && systemPrefersDark);
  const resolvedScheme: ResolvedColorScheme =
    isDarkAvailable && wantsDark ? 'dark' : 'light';

  const setPreference = useCallback(
    (next: ColorSchemePreference) => {
      setPendingPreference(next);
      if (!settingsRef) {
        console.debug('[ColorScheme] no signed-in user; preference not stored');
        return;
      }
      setDoc(settingsRef, {colorScheme: next}, {merge: true}).catch(e => {
        console.debug('[ColorScheme] failed to persist preference', next, e);
      });
    },
    [settingsRef],
  );

  const value = useMemo<ColorSchemeContextData>(
    () => ({preference, resolvedScheme, isDarkAvailable, setPreference}),
    [preference, resolvedScheme, isDarkAvailable, setPreference],
  );

  return (
    <ColorSchemeContext.Provider value={value}>
      {children}
    </ColorSchemeContext.Provider>
  );
}

/**
 * Returns the active color-scheme state.
 *
 * @return The nearest {@link ColorSchemeContextData}. Without a provider,
 *     returns a light-only value whose setter is a no-op.
 * @throws Never.
 */
export function useColorScheme(): ColorSchemeContextData {
  return useContext(ColorSchemeContext);
}

/**
 * Applies the resolved color scheme to Bootstrap while the calling surface is
 * mounted and `isEnabled` is true: sets `data-bs-theme="dark"` on `<html>` so
 * Bootstrap's components (modals, dropdowns, form controls, cards, including
 * ones rendered in portals) switch to their dark variants, and removes it on
 * unmount or when the scheme resolves to light.
 *
 * @param isEnabled - Whether this surface wants the dark scheme applied. Pass
 *     `false` to leave Bootstrap alone (for example on full-screen focus pages
 *     that manage their own look).
 * @return Nothing.
 * @throws Never.
 */
export function useBootstrapColorScheme(isEnabled: boolean): void {
  const {resolvedScheme} = useColorScheme();
  const applyDark = isEnabled && resolvedScheme === 'dark';
  useEffect(() => {
    if (!applyDark) {
      return;
    }
    const root = document.documentElement;
    root.setAttribute('data-bs-theme', 'dark');
    return () => {
      root.removeAttribute('data-bs-theme');
    };
  }, [applyDark]);
}
